import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { useAuth } from '@/hooks/useAuth';
import { PaymentService } from '@/supabase/payment/paymentService';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { supabase } from '@/supabase/client';
import { getPaymentErrorMessage } from '@/lib/payment/utils';
import { tiktokTrackSubscribe } from '@/lib/tiktok';

// 결제 상태 타입
type PaymentStatus = 'LOADING' | 'SUCCESS' | 'FAILURE' | 'CANCELED';

// 상태별 메시지 정의
const STATUS_MESSAGES = {
  LOADING: '처리 중입니다...',
  SUCCESS: '카드 등록이 완료되었습니다.',
  FAILURE: '처리 중 문제가 발생했습니다.',
  CANCELED: '카드 등록이 취소되었습니다.'
} as const;

export default function PaymentCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { refreshSubscription } = useSubscription();
  const [paymentService] = useState(() => new PaymentService());
  
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>('LOADING');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  
  // 세션 복구 시도
  useEffect(() => {
    const refreshSession = async () => {
      try {
        await supabase.auth.getSession();
      } catch (err) {
        toast.error('세션 확인 중 오류 발생');
      }
    };
    
    refreshSession();
  }, []);
  
  useEffect(() => {
    // URL 쿼리 파라미터 파싱
    const searchParams = new URLSearchParams(location.search);
    
    // billingKey 파라미터 처리
    const billingKey = searchParams.get('billingKey');
    const transactionType = searchParams.get('transactionType');
    
    // 오류 관련 파라미터
    const code = searchParams.get('code');
    const message = searchParams.get('message');
    const pgMessage = searchParams.get('pgMessage');
    
    // 구독 플랜 ID (커스텀 파라미터)
    const planId = searchParams.get('planId');
    
    // 중복 처리 방지를 위한 플래그
    const handlePaymentResult = async () => {
      // 중복 처리 방지
      if (isProcessing) {
        return;
      }
      setIsProcessing(true);
      
      try {
        // 사용자 세션 확인 및 복구 시도
        let currentUser = user;
        if (!currentUser) {
          // 세션 정보 직접 조회
          const { data } = await supabase.auth.getSession();
          if (data.session?.user) {
            currentUser = data.session.user;
          } else {
            // 세션이 없는 경우 - 방문자로 처리
            setPaymentStatus('FAILURE');
            toast.error('로그인 정보를 찾을 수 없어 카드 등록에 실패했습니다.');
            
            // 잠시 후 마이페이지로 리다이렉트
            setTimeout(() => {
              navigate('/mypage');
            }, 3000);
            return;
          }
        }
        
        // 오류 코드가 있는 경우 - 실패 처리
        if (code) {
          setPaymentStatus('FAILURE');
          
          // PG사 오류 메시지가 있으면 그것을 표시, 없으면 일반 오류 메시지
          const errorMsg = pgMessage || message || STATUS_MESSAGES.FAILURE;
          setErrorMessage(errorMsg);
          toast.error(errorMsg);
          
          // 잠시 후 마이페이지로 리다이렉트
          setTimeout(() => {
            navigate('/mypage');
          }, 3000);
          return;
        }
        
        // 빌링키 발급 성공 케이스
        if (billingKey && transactionType === 'ISSUE_BILLING_KEY') {
          if (currentUser) {
            try {
              // 빌링키 저장
              await paymentService.registerBillingKey(currentUser.id, billingKey);
              
              // 플랜 ID가 URL 파라미터로 전달된 경우 결제 진행
              if (planId) {
                try {
                  // PaymentService의 통합 메서드를 사용하여 결제 및 구독 처리
                  await paymentService.processBillingKeyAndSubscribe(
                    currentUser.id,
                    billingKey,
                    planId
                  );
                  
                  toast.success('구독이 성공적으로 시작되었습니다.');
                  
                  // 틱톡 구독 이벤트 전송
                  const plan = await paymentService.getSubscriptionPlan(planId);
                  if (plan) {
                    tiktokTrackSubscribe(plan);
                  }
                } catch (err) {
                  // 오류 메시지 개선 - 공통 유틸리티 함수 사용
                  toast.error(getPaymentErrorMessage(err));
                }
              }
              
              // 결제 성공 플래그 설정
              localStorage.setItem('payment_success', 'true');
              localStorage.setItem('payment_time', Date.now().toString());
              
              setPaymentStatus('SUCCESS');
              toast.success(STATUS_MESSAGES.SUCCESS);
            } catch (err) {
              setPaymentStatus('FAILURE');
              toast.error('카드 등록에 실패했습니다');
            }
          }
        } else {
          // 빌링키 파라미터가 없는 경우
          setPaymentStatus('CANCELED');
          toast.error('카드 등록이 취소되었습니다.');
        }
      } catch (error) {
        // 오류 메시지 개선 - 공통 유틸리티 함수 사용
        const errorMsg = getPaymentErrorMessage(error);
        setErrorMessage(errorMsg);
        toast.error(errorMsg);
        
        setPaymentStatus('FAILURE');
      } finally {
        // 구독 정보 새로고침
        if (user) {
          try {
            await refreshSubscription();
          } catch (err) {
            toast.error('구독 정보 새로고침 실패');
          }
        }
        
        // 토스트 메시지 중복 방지 플래그 저장
        localStorage.setItem('payment_processed', 'true');
        
        // 잠시 후 마이페이지로 리다이렉트
        setTimeout(() => {
          navigate('/mypage');
        }, 3000);
      }
    };
    
    handlePaymentResult();
  }, [location.search, user, navigate, refreshSubscription, paymentService, isProcessing]);
  
  // 상태별 메시지 및 아이콘 표시
  const renderStatusMessage = () => {
    switch (paymentStatus) {
      case 'SUCCESS':
        return (
          <>
            <div className="mb-4 text-green-500">
              <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-green-600">{STATUS_MESSAGES.SUCCESS}</h1>
          </>
        );
      case 'FAILURE':
        return (
          <>
            <div className="mb-4 text-red-500">
              <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-red-600">{STATUS_MESSAGES.FAILURE}</h1>
            {errorMessage && <p className="mt-2 text-red-500">{errorMessage}</p>}
          </>
        );
      case 'CANCELED':
        return (
          <>
            <div className="mb-4 text-yellow-500">
              <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-yellow-600">{STATUS_MESSAGES.CANCELED}</h1>
          </>
        );
      default:
        return (
          <>
            <div className="mb-4">
              <LoadingSpinner size="lg" />
            </div>
            <h1 className="text-2xl font-bold text-gray-600">{STATUS_MESSAGES.LOADING}</h1>
            <p className="mt-2 text-gray-500">결제 정보를 처리하고 있습니다...</p>
          </>
        );
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="bg-white rounded-lg shadow-md p-8 max-w-md w-full text-center">
        {renderStatusMessage()}
        <p className="mt-6 text-gray-600">
          {paymentStatus === 'LOADING' 
            ? '잠시만 기다려주세요...' 
            : '잠시 후 마이페이지로 이동합니다.'}
        </p>
      </div>
    </div>
  );
} 